import React, { useState, useEffect } from "react";
import "./MyCalls.scss";
import { useUnit } from "effector-react";
import {
  $callsStore,
  fetchDeclaredInterestCallsRequested,
  fetchBookmarkedCallsRequested,
  fetchArchivedCallsRequested,
  loadMoreDeclaredInterestCallsRequested,
  loadMoreArchivedCallsRequested,
  updateCallBookmark,
} from "../store";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { Link } from "react-router-dom";
import { createRoute, ROUTES } from "@/core/constants/app";
import { slugify } from "@/core/utils/stringUtils";
import { imageList } from "@/core/utils/imageList";
import Loader from "@/core/components/loader/Loader";
import { BgImage } from "@/core/components/bgImage/BgImage";
import { formatRelativeTime } from "@/core/utils/format";
import { Tags } from "@/core/components/tags/Tags";
import { useCallsViewModel } from "../viewmodel/useCallsViewModel";
import { $userBasicInfo } from "@/features/user/store";

type InterestTabType = "active" | "expired";

export const MyCalls: React.FC = () => {
  const [activeInterestTab, setActiveInterestTab] =
    useState<InterestTabType>("active");
  const [userBasicInfo] = useUnit([$userBasicInfo]);
  const { pro_user } = userBasicInfo || {};

  const getScoreColor = (score: number) => {
    if (score >= 67) return "#067647"; // green
    if (66 >= score && score >= 34) return "#f97316"; // orange
    return "#ef4444"; // red
  };
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(
    new Set(),
  );
  const callsState = useUnit($callsStore);
  const [
    fetchDeclaredInterest,
    fetchBookmarked,
    fetchArchived,
    loadMoreDeclaredInterest,
    loadMoreArchived,
  ] = useUnit([
    fetchDeclaredInterestCallsRequested,
    fetchBookmarkedCallsRequested,
    fetchArchivedCallsRequested,
    loadMoreDeclaredInterestCallsRequested,
    loadMoreArchivedCallsRequested,
  ]);
  useEffect(() => {
    // Fetch all three types of calls when component mounts
    fetchDeclaredInterest(undefined);
    fetchBookmarked(undefined);
    fetchArchived(undefined);
  }, [fetchDeclaredInterest, fetchBookmarked, fetchArchived]);

  const handleLoadMoreDeclaredInterest = () => {
    loadMoreDeclaredInterest(undefined);
  };
  const handleBookmarkAction = async (
    callId: string,
    isBookmarked: boolean,
  ) => {
    // The effect handles the API call, just trigger the event
    updateCallBookmark({ callId, isBookmarked: !isBookmarked });
    setTimeout(() => {
      (fetchDeclaredInterestCallsRequested(),
        fetchBookmarkedCallsRequested(),
        fetchArchivedCallsRequested());
    }, 200);
  };
  const handleLoadMoreArchived = () => {
    loadMoreArchived(undefined);
  };

  const getDaysLeftInfo = (deadline: string) => {
    try {
      if (!deadline)
        return { daysLeft: 0, cssClass: "badgeYellow", text: "No deadline" };

      const now = new Date();
      const targetDate = new Date(deadline);

      if (isNaN(targetDate.getTime())) {
        return { daysLeft: 0, cssClass: "badgeYellow", text: "Invalid date" };
      }

      const diffTime = targetDate.getTime() - now.getTime();
      const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const cssClass = daysLeft > 39 ? "badgeGreen" : "badgeYellow";
      const text = formatRelativeTime(deadline);

      return { daysLeft, cssClass, text };
    } catch (error) {
      return { daysLeft: 0, cssClass: "badgeYellow", text: "Error" };
    }
  };

  const toggleSectorExpansion = (callId: string) => {
    setExpandedSectors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  const renderCallsList = (
    calls: any[],
    loading: boolean,
    loadMore: boolean,
    hasMore: boolean,
    onLoadMore?: () => void,
  ) => {
    if (loading && calls.length === 0) {
      return <Loader message="Loading calls..." />;
    }

    return (
      <>
        <div className="calls-list grid-view">
          {calls.map((call) => {
            const daysInfo = getDaysLeftInfo(call.deadline);

            return (
              <div
                key={`${slugify(call.title)}-${call.id}`}
                className="call-item eu-card"
              >
                <Link to={createRoute(ROUTES.CALL_DETAIL, { id: call.id })}>
                  <div className="call-header">
                    <div className="call-type">
                      <div className="eu-flag">
                        <img src={call?.programme?.logo} alt="EU call" />
                      </div>
                      <span>{call?.programme?.name}</span>
                    </div>
                    <button
                      className={`bookmark-btn ${
                        call.is_call_bookmarked ||
                        call.is_call_declared_interest
                          ? "bookmarked"
                          : ""
                      } ${call.is_call_declared_interest ? "declared-interest" : ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        !call.is_call_declared_interest &&
                          handleBookmarkAction(
                            call.id,
                            call.is_call_bookmarked ||
                              call.is_call_declared_interest,
                          );
                      }}
                      disabled={call.is_call_declared_interest}
                    >
                      <SvgIcon
                        icon={
                          call.is_call_declared_interest
                            ? "icon-declare"
                            : call.is_call_bookmarked
                              ? "icon-bookmarked"
                              : "icon-bookmark"
                        }
                        className={
                          call.is_call_declared_interest ? "declare-icon" : ""
                        }
                      />
                    </button>
                  </div>

                  <div className="call-content">
                    <div className="call-badges">
                      {call.is_new && (
                        <span className="badge new-badge">New</span>
                      )}
                      {daysInfo.daysLeft < 40 && daysInfo.daysLeft > 0 && (
                        <Tags
                          className={"badgeRed"}
                          type="custom"
                          text={"Expiring soon"}
                        />
                      )}
                      <span className="badge deadline-badge">
                        <SvgIcon icon="icon-clock" /> until{" "}
                        {new Date(call.deadline).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                      <div className={`badge daysLeft ${daysInfo.cssClass}`}>
                        <Tags
                          className={daysInfo.cssClass}
                          type="custom"
                          text={daysInfo.text}
                        />
                      </div>
                    </div>

                    <h3 className="call-title">{call.title}</h3>

                    <div className="call-budget">
                      € {parseInt(call.budget).toLocaleString()}
                    </div>
                    <div className="interested-partners">
                      <span className="partners-label">
                        Interested Partners
                      </span>
                      {/* {!call?.interested_partners ||
                        (call?.interested_partners.length === 0 && "0")} */}
                      {call?.interested_partners &&
                        call?.interested_partners?.length > 0 && (
                          <div className="partners-avatars">
                            {!pro_user && (
                              <Tags
                                type={"partner"}
                                number={call?.interested_partners?.length}
                              />
                            )}
                            {pro_user &&
                              call?.interested_partners
                                ?.slice(0, 4)
                                .map((p: any, i: number) => (
                                  <div
                                    key={p.id || i}
                                    className="partner-avatar-wrapper"
                                    style={{
                                      transform:
                                        i > 0
                                          ? `translateX(-${10 * i}px)`
                                          : "none",
                                      zIndex: i > 0 ? 2 + (i - 1) : 1,
                                    }}
                                  >
                                    <div className="company-logo placeholder">
                                      <BgImage
                                        img={p.logo_uri}
                                        altImage={imageList.noOrgimage}
                                      />
                                    </div>
                                  </div>
                                ))}

                            {pro_user &&
                              call.interested_partners &&
                              call.interested_partners?.length > 4 && (
                                <span
                                  className="more-count-small"
                                  style={{
                                    transform: `translateX(-${12 * call.interested_partners.slice(0, 4).length}px)`,
                                    zIndex: 2 + call.interested_partners.length,
                                  }}
                                >
                                  +{call.interested_partners.length - 4}
                                </span>
                              )}
                          </div>
                        )}
                    </div>
                    <div className="sectors-tags">
                      {expandedSectors.has(call.id) ? (
                        <>
                          {call.sectors.map((sector: any) => (
                            <span
                              key={`${call.id}-${slugify(sector.name)}-${sector.id}`}
                              className="sector-tag"
                            >
                              {sector.name}
                            </span>
                          ))}
                          <span
                            className="sector-tag more-count"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleSectorExpansion(call.id);
                            }}
                          >
                            less
                          </span>
                        </>
                      ) : (
                        <>
                          {call.sectors.slice(0, 4).map((sector: any) => (
                            <span
                              key={`${call.id}-${slugify(sector.name)}-${sector.id}`}
                              className="sector-tag"
                            >
                              {sector.name}
                            </span>
                          ))}
                          {call.sectors.length > 4 && (
                            <span
                              className="sector-tag more-count"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleSectorExpansion(call.id);
                              }}
                            >
                              +{call.sectors.length - 4}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="call-footer">
                    {typeof call?.max_score === "string" && pro_user ? (
                      <div
                        className="match-badge"
                        style={{
                          backgroundColor: getScoreColor(call.max_score),
                        }}
                      >{`${call?.max_score}% match`}</div>
                    ) : (
                      <div></div>
                    )}
                    <div className="call-stats">
                      <div className="stat-item item-comments">
                        <SvgIcon icon="icon-message" /> {call.comments_count}{" "}
                        comments
                      </div>
                      <div className="stat-item item-ideas">
                        <SvgIcon icon="icon-ideas" /> {call.ideas_count} ideas
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
        {hasMore && onLoadMore && (
          <div className="calls-action">
            {loadMore ? (
              <Loader message="Loading" />
            ) : (
              <button
                className="calls-loadMore"
                onClick={onLoadMore}
                disabled={loadMore}
              >
                Load more
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  const currentInterestCalls =
    activeInterestTab === "active"
      ? callsState.declaredInterestCalls
      : callsState.archivedCalls;

  const currentInterestLoading =
    activeInterestTab === "active"
      ? callsState.declaredInterestLoading
      : callsState.archivedLoading;

  const currentInterestLoadMore =
    activeInterestTab === "active"
      ? callsState.declaredInterestLoadMore
      : callsState.archivedLoadMore;

  const currentInterestHasMore =
    activeInterestTab === "active"
      ? callsState.declaredInterestCalls.length <
        callsState.declaredInterestPagination.total
      : callsState.archivedCalls.length < callsState.archivedPagination.total;

  const currentInterestLoadMoreHandler =
    activeInterestTab === "active"
      ? handleLoadMoreDeclaredInterest
      : handleLoadMoreArchived;

  return (
    <div className="my-calls">
      {/* First Section: Calls you have declared interest for */}
      <div className="my-calls-section">
        <div className="section-header">
          {currentInterestCalls.length > 0 && (
            <h2 className="section-title">
              {activeInterestTab === "active"
                ? "Calls you have declared interest for"
                : "Calls you have saved or declared interest and have expired"}
            </h2>
          )}

          {/* Active/Expired tabs - positioned on the right */}
          <div className="interest-tabs">
            <button
              className={`tab-button ${activeInterestTab === "active" ? "active" : ""}`}
              onClick={() => setActiveInterestTab("active")}
            >
              {activeInterestTab === "active" && (
                <span className="tab-dot"></span>
              )}
              Active
              {/* ({callsState.declaredInterestPagination.total}) */}
            </button>
            <button
              className={`tab-button ${activeInterestTab === "expired" ? "active" : ""}`}
              onClick={() => setActiveInterestTab("expired")}
            >
              {activeInterestTab === "expired" && (
                <span className="tab-dot"></span>
              )}
              Expired
              {/* ({callsState.archivedPagination.total}) */}
            </button>
          </div>
        </div>

        {/* Content for active/expired calls */}

        <div className="interest-content">
          {renderCallsList(
            currentInterestCalls,
            currentInterestLoading,
            currentInterestLoadMore,
            currentInterestHasMore,
            currentInterestLoadMoreHandler,
          )}
        </div>
      </div>

      {/* Second Section: Calls you have saved - only show when Active tab is selected */}
      {activeInterestTab === "active" && (
        <div className="my-calls-section">
          {callsState.bookmarkedCalls.length > 0 && (
            <h2 className="section-title">Calls you have saved</h2>
          )}

          <div className="bookmarked-content">
            {renderCallsList(
              callsState.bookmarkedCalls,
              callsState.bookmarkedLoading,
              callsState.bookmarkedLoadMore,
              callsState.bookmarkedCalls.length <
                callsState.bookmarkedPagination.total,
            )}
          </div>
        </div>
      )}
      {currentInterestCalls.length === 0 &&
        callsState.bookmarkedCalls.length === 0 && (
          <div>
            <div className="no-calls">
              <div className="no-calls-wrapper">
                <SvgIcon icon="icon-briefcase" />

                <div className="no-calls-title">
                  {/* You haven't saved {callsState.declaredInterestCalls.length === 0 && 'or declared interest'} to any call. */}
                  You haven’t saved or declared interest to any call
                </div>

                <div className="no-calls-text">
                  You can save or declare interest to any call you can find in{" "}
                  <strong>All calls</strong> and the <strong>For you</strong>{" "}
                  tabs
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
